<template>
  <div id="loaderWrapper">
    <div class="d-flex justify-content-between align-items-center p-4">
      <div class="card-header border-0 pl-3 pt-2">
        <h2 class="mb-0">{{ vueTranslator('management.topic.calendar_title') }}</h2>
        <p class="text-gray mb-0">
          {{ vueTranslator('management.topic.calendar_description') }}
        </p>
      </div>

      <a class="btn btn-orange btn-sm" :href="'/' + props.locale + '/manager-portal/management-module'" target="_blank">
        {{ vueTranslator('management.topic.go_to_module') }}
      </a>
    </div>
    <Calendar
      :data-source="dataSource"
      :year="currentYear"
      :language="language"
      :context-menu-items="contextMenuItems"
      @click-day="clickDay"
      @year-changed="changeSelectedYear"
    ></Calendar>

    <div class="card-footer d-flex justify-content-between align-items-center pt-6">
      <div class="d-flex">
        <div class="mr-4">
          <input class="form-check-input legend legend-orange mr-2" type="checkbox" disabled>
          <span>{{ vueTranslator('management.topic.status_in_progress') }}</span>
        </div>
        <div class="mr-4">
          <input class="form-check-input legend legend-green mr-2" type="checkbox" disabled>
          <span>{{ vueTranslator('management.topic.status_completed') }}</span>
        </div>
        <div class="mr-4">
          <input class="form-check-input legend legend-red mr-2" type="checkbox" disabled>
          <span>{{ vueTranslator('management.topic.status_overdue') }}</span>
        </div>
        <div class="mr-4">
          <input class="form-check-input legend legend-gray mr-2" type="checkbox" disabled>
          <span>{{ vueTranslator('management.topic.status_planned') }}</span>
        </div>
      </div>
    </div>

    <div v-if="showModal" @click.self="closeModal" class="year-calendar-modal">
      <div class="modal-content">
        <div class="modal-body">
          <div class="d-flex justify-content-between">
            <p class="text-dark fw-bolder">{{ vueTranslator('management.topic.modal_title') }}</p>
            <i @click="closeModal" class="icon-close"></i>
          </div>

          <div v-for="modalEvent in modalEvents" :key="modalEvent.topicId" class="event_row mt-3">
            <div class="info__row border-bottom-dashed">
              <span>{{ vueTranslator('management.topic.topic_label') }}</span>
              <span class="fw-bolder">{{ modalEvent.title }}</span>
            </div>
            <div class="info__row border-bottom-dashed">
              <span>{{ vueTranslator('management.topic.section_label') }}</span>
              <span class="fw-bolder">{{ modalEvent.sectionName }}</span>
            </div>
            <div class="info__row border-bottom-dashed">
              <span>{{ vueTranslator('management.topic.due_date_label') }}</span>
              <span class="fw-bolder">{{ modalEvent.dueDate }}</span>
            </div>
            <div class="info__row">
              <span>{{ vueTranslator('management.topic.status_label') }}</span>
              <span class="fw-bolder" :class="'status-' + modalEvent.status">{{ getStatusLabel(modalEvent.status) }}</span>
            </div>

            <div class="d-flex justify-content-end mb-3">
              <a :href="'/' + props.locale + '/manager-portal/management-module/topic/' + modalEvent.topicId" target="_blank" class="btn-view-booking mt-3">
                {{ vueTranslator('management.topic.view_topic') }}

                <span class="svg-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect opacity="0.5" x="18" y="13" width="13" height="2" rx="1" transform="rotate(-180 18 13)" fill="currentColor"></rect>
                    <path d="M15.4343 12.5657L11.25 16.75C10.8358 17.1642 10.8358 17.8358 11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25L18.2929 12.7071C18.6834 12.3166 18.6834 11.6834 18.2929 11.2929L12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75C10.8358 6.16421 10.8358 6.83579 11.25 7.25L15.4343 11.4343C15.7467 11.7467 15.7467 12.2533 15.4343 12.5657Z" fill="currentColor"></path>
                  </svg>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="isLoading" class="spinner-overlay">
    <div class="spinner"></div>
  </div>
</template>

<script setup>
import {ref, onMounted} from 'vue';
import Calendar from './YearCalendarWidget.vue';
import {vueTranslator} from '../mixins.js';

const props = defineProps({
  locale: {
    type: String,
    default: 'nl'
  }
});

const language = ref(props.locale);

// Status priority (higher = worse)
const STATUS_PRIORITY = {
  'completed': 0,
  'to_do': 1,
  'in_progress': 2,
  'overdue': 3,
};

const STATUS_COLORS = {
  'completed': '#28a745',
  'to_do': '#6c757d',
  'in_progress': '#FFA500',
  'overdue': '#dc3545',
};

const showModal = ref(false);
const isLoading = ref(false);
const modalEvents = ref([]);

const dataSource = ref([]);

const currentYear = ref(new Date().getFullYear());

function changeSelectedYear(year) {
  if (currentYear.value !== year.currentYear) {
    currentYear.value = year.currentYear;
  }
}

function processDataWithPriorityColors(data) {
  // Group events by date
  const eventsByDate = {};
  data.forEach(event => {
    const dateKey = event.startDate.toISOString().split('T')[0];
    if (!eventsByDate[dateKey]) {
      eventsByDate[dateKey] = [];
    }
    eventsByDate[dateKey].push(event);
  });

  // Set color based on worst status for each day
  Object.values(eventsByDate).forEach(events => {
    const worstStatus = events.reduce((worst, event) => {
      return STATUS_PRIORITY[event.status] > STATUS_PRIORITY[worst]
        ? event.status
        : worst;
    }, 'completed');

    const dayColor = STATUS_COLORS[worstStatus];
    events.forEach(event => {
      event.color = dayColor;
    });
  });

  return data;
}

async function getManagementCalendarData() {
  const url = `/${props.locale}/manager-portal/dashboard/management-calendar/data`;

  isLoading.value = true;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Bad fetch response');
    }

    const responseData = await response.json();

    responseData.forEach((data) => {
      const formattedStartDate = new Date(data.startDate).toISOString();
      const formattedEndDate = new Date(data.endDate).toISOString();

      data.name = data.title;
      data.startDate = new Date(formattedStartDate);
      data.startDate.setHours(0, 0, 0, 0);
      data.endDate = new Date(formattedEndDate);
      data.endDate.setHours(0, 0, 0, 0);
    });

    dataSource.value = processDataWithPriorityColors(responseData);
  } catch (error) {
    dataSource.value = [];
  } finally {
    isLoading.value = false;
  }
}

const contextMenuItems = [
  {
    click: () => {
      showModal.value = true;
    },
  },
];

const clickDay = (event) => {
  if (event.events.length > 0) {
    modalEvents.value = [];

    event.events.forEach((evt) => {
      modalEvents.value.push({
        topicId: evt.topicId,
        title: evt.title,
        sectionName: evt.sectionName,
        dueDate: moment(evt.startDate).format('DD-MM-YYYY'),
        status: evt.status,
      });
    });

    showModal.value = true;
  }
};

const closeModal = () => {
  showModal.value = false;
};

const getStatusLabel = (status) => {
  return vueTranslator(`management.topic.status_${status}`) || status;
};

onMounted(async () => {
  await getManagementCalendarData();
});
</script>

<style scoped>
.legend-orange {
  background-color: #FFA500 !important;
  border-color: #FFA500 !important;
}

.legend-green {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

.legend-red {
  background-color: #dc3545 !important;
  border-color: #dc3545 !important;
}

.legend-gray {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

.status-in_progress {
  color: #FFA500;
}

.status-completed {
  color: #28a745;
}

.status-overdue {
  color: #dc3545;
}

.status-planned {
  color: #6c757d;
}
</style>
